import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { MODULE_QUESTIONS } from "@/data/moduleQuestions";
import ModuleRemediationScreen from "./ModuleRemediationScreen";
import { AlertTriangle, ArrowRight, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQAOverride } from "@/hooks/useQAOverride";
import FinancialTermSupport, { InlineFinancialText } from "@/components/lesson/FinancialTermSupport";
import { useSfx } from "@/hooks/useSfx";
import { filterVocabForContext, getLessonVocabulary } from "@/data/lessonVocabularyRegistry";

interface Props {
  moduleKey: string;
  onPass: () => void;
  /** Optional: sample N questions from the bank instead of using all. */
  sampleSize?: number;
  /** Optional: shuffle answer options per question (correctIndex is remapped). */
  shuffleOptions?: boolean;
  /** Where the remediation screen's "back" button navigates. */
  backHref?: string;
  /** Label for the remediation screen's "back" button. */
  backLabel?: string;
}

export default function ModuleQuiz({ moduleKey, onPass, sampleSize, shuffleOptions, backHref, backLabel }: Props) {
  const { user } = useAuth();
  const { isQAOverride } = useQAOverride();
  const { playSfx } = useSfx();
  const rawQuestions = MODULE_QUESTIONS[moduleKey] || [];
  // Stable per-mount sample + per-question option shuffle.
  const questions = useMemo(() => {
    let pool = rawQuestions.slice();
    if (sampleSize && pool.length > sampleSize) {
      pool = pool
        .map((q) => ({ q, r: Math.random() }))
        .sort((a, b) => a.r - b.r)
        .slice(0, sampleSize)
        .map((x) => x.q);
    }
    if (shuffleOptions) {
      pool = pool.map((q) => {
        const indexed = q.options.map((opt, i) => ({ opt, i, r: Math.random() }));
        indexed.sort((a, b) => a.r - b.r);
        const newOptions = indexed.map((x) => x.opt);
        const newCorrect = indexed.findIndex((x) => x.i === q.correctIndex);
        return { ...q, options: newOptions, correctIndex: newCorrect };
      });
    }
    return pool;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey, sampleSize, shuffleOptions]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [showRemediation, setShowRemediation] = useState(false);
  const [activeTerm, setActiveTerm] = useState<string | null>(null);

  const totalQuestions = questions.length;
  const passingScore = Math.ceil(totalQuestions * 0.8);
  const q = questions[currentIdx] ?? {
    question: "",
    options: [] as string[],
    explanation: "",
    correctIndex: 0,
  };
  const lessonVocab = useMemo(() => {
    const all = getLessonVocabulary(moduleKey);
    return filterVocabForContext(all, { surface: "quiz", questionText: q.question });
  }, [moduleKey, q.question]);

  if (totalQuestions === 0) {
    return (
      <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-5 text-center space-y-3">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-destructive/25 bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <div className="space-y-1">
          <h2 className="font-heading text-lg font-bold text-foreground">Knowledge Check unavailable</h2>
          <p className="text-sm text-muted-foreground">
            This module requires a Knowledge Check, but no questions are configured. The module was not completed.
          </p>
        </div>
      </div>
    );
  }

  const handleSelect = (optIdx: number) => {
    if (answered) return;
    setSelectedIdx(optIdx);
    setAnswered(true);
    if (optIdx === q.correctIndex) {
      setCorrectCount((prev) => prev + 1);
      playSfx("correct_soft");
    } else {
      playSfx("incorrect_soft");
    }
  };

  const saveCompletion = async (score: number, _passed: boolean) => {
    if (!user) return;
    // QA admins replay modules for testing — do not write completion records.
    if (isQAOverride) return;
    // `passed` is a money-bearing outcome, so the server derives it from the
    // submitted score: clients can no longer write module_completions directly.
    const { error } = await supabase.rpc("record_module_completion" as never, {
      p_module_key: moduleKey,
      p_score: score,
      p_max_score: totalQuestions,
    } as never);
    if (error) console.warn("[ModuleQuiz] completion not recorded:", error.message);
  };


  const handleNext = async () => {
    playSfx("click_primary");
    if (currentIdx + 1 >= totalQuestions) {
      const finalCorrect = correctCount;
      const passed = finalCorrect >= passingScore;
      // Awaited: downstream money RPCs require the completion row to exist first.
      await saveCompletion(finalCorrect, passed);
      if (passed) {
        onPass();
      } else {
        setShowRemediation(true);
      }
      return;
    }


    setCurrentIdx((prev) => prev + 1);
    setSelectedIdx(null);
    setAnswered(false);
    setActiveTerm(null);
  };

  const handleRetry = () => {
    setCurrentIdx(0);
    setSelectedIdx(null);
    setAnswered(false);
    setCorrectCount(0);
    setShowRemediation(false);
    setActiveTerm(null);
  };

  if (showRemediation) {
    return (
      <ModuleRemediationScreen
        moduleKey={moduleKey}
        score={correctCount}
        maxScore={totalQuestions}
        onRetry={handleRetry}
        backHref={backHref}
        backLabel={backLabel}
      />
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      <div className="space-y-2 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10">
          <HelpCircle className="h-6 w-6 text-primary" />
        </div>
        <h2 className="font-heading text-lg font-bold text-foreground">Knowledge Check</h2>
        <p className="text-xs text-muted-foreground">
          Question {currentIdx + 1} of {totalQuestions} · Need {passingScore}/{totalQuestions} to pass
        </p>
      </div>

      <div className="flex justify-center gap-1.5">
        {questions.map((_, i) => (
          <div
            key={i}
            className={`h-2 w-2 rounded-full transition-colors ${
              i < currentIdx ? "bg-primary" : i === currentIdx ? "bg-primary/50 ring-2 ring-primary/20" : "bg-muted"
            }`}
          />
        ))}
      </div>

      <FinancialTermSupport
        lessonTerms={lessonVocab}
        activeTerm={activeTerm}
        onSelectTerm={setActiveTerm}
        label="Concept Support"
        surfaceHint="Use these to reason through the question — they will not give away the answer."
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentIdx}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          <div className="rounded-2xl border border-border/60 bg-card/50 p-4">
            <InlineFinancialText
              text={q.question}
              onSelectTerm={setActiveTerm}
              className="text-sm font-medium leading-relaxed text-foreground"
            />
          </div>

          <div className="space-y-2">
            {q.options.map((opt, i) => {
              let borderClass = "border-border bg-card/50 hover:border-primary/30";
              let iconEl = null;

              if (answered) {
                if (i === q.correctIndex) {
                  borderClass = "border-primary/40 bg-primary/10";
                  iconEl = <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />;
                } else if (i === selectedIdx && i !== q.correctIndex) {
                  borderClass = "border-destructive bg-destructive/10";
                  iconEl = <XCircle className="h-4 w-4 shrink-0 text-destructive" />;
                } else {
                  borderClass = "border-border/50 bg-card/30 opacity-60";
                }
              } else if (i === selectedIdx) {
                borderClass = "border-primary bg-primary/10";
              }

              return (
                <button
                  key={i}
                  onClick={() => handleSelect(i)}
                  disabled={answered}
                  className={`flex w-full items-start gap-3 rounded-2xl border p-3 text-left text-sm transition-all ${borderClass}`}
                >
                  <span className="mt-0.5 text-xs font-semibold text-primary">{String.fromCharCode(65 + i)}.</span>
                  <span className="flex-1 break-words text-foreground">{opt}</span>
                  {iconEl}
                </button>
              );
            })}
          </div>

          {answered && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-border bg-card/60 p-4"
            >
              <InlineFinancialText
                text={q.explanation}
                onSelectTerm={setActiveTerm}
                className="text-xs leading-relaxed text-muted-foreground"
              />
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      {answered && (
        <Button onClick={() => void handleNext()} className="w-full gap-2">
          {currentIdx + 1 >= totalQuestions ? "See Results" : "Next Question"}
          <ArrowRight className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
